import { supabase } from "../supabase/client";
import { isAbortError } from "../utils/abort";

const resolveApiRoot = () => {
  if (typeof window !== "undefined") {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (isLocal) {
      return "/api";
    }
  }
  return process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : "/api";
};

const API_ROOT = resolveApiRoot();

type ConsoleLevel = "log" | "info" | "warn" | "error" | "debug";

interface LogPayload {
  level: ConsoleLevel;
  message: string;
  args: string[];
  context: {
    url: string;
    userAgent: string;
  };
  timestamp: string;
}

const buildSafeString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Error) {
    return `${value.name}: ${value.message}\n${value.stack ?? ""}`.trim();
  }
  try {
    const seen = new Set();
    return JSON.stringify(value, (_key, val) => {
      if (typeof val === "bigint") {
        return val.toString();
      }
      if (typeof val === "object" && val !== null) {
        if (seen.has(val)) {
          return "[Circular]";
        }
        seen.add(val);
      }
      return val;
    });
  } catch {
    return String(value);
  }
};

const createMessage = (args: unknown[]): string =>
  args.map((arg) => buildSafeString(arg)).join(" ");

export const initConsoleReporter = (): void => {
  if (typeof window === "undefined") return;

  const original = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  };

  const queue: LogPayload[] = [];
  let flushTimer: number | null = null;
  let isFlushing = false;
  const maxQueueSize = 200;
  const batchSize = 10;

  const buildPayload = (level: ConsoleLevel, args: unknown[]): LogPayload => {
    const normalizedLevel =
      level === "log" ? "info" : level === "debug" ? "debug" : level;
    return {
      level: normalizedLevel,
      message: createMessage(args),
      args: args.map((arg) => buildSafeString(arg)),
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
      timestamp: new Date().toISOString(),
    };
  };

  const scheduleFlush = () => {
    if (flushTimer !== null) return;
    flushTimer = window.setTimeout(() => {
      flushTimer = null;
      void flushQueue();
    }, 500);
  };

  const enqueue = (payload: LogPayload) => {
    queue.push(payload);
    if (queue.length > maxQueueSize) {
      queue.splice(0, queue.length - maxQueueSize);
    }
    scheduleFlush();
  };

  const sendBatch = async (
    batch: LogPayload[],
    useBeacon: boolean
  ): Promise<boolean> => {
    const body = JSON.stringify(batch.length === 1 ? batch[0] : batch);
    const bodySize = new Blob([body]).size;
    const keepalive = bodySize < 45000;

    if (useBeacon) {
      useBeacon = false;
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch {
      // Ignore auth lookup failures, still post logs.
    }

    const response = await fetch(`${API_ROOT}/logs/console`, {
      method: "POST",
      headers,
      body,
      keepalive,
    });
    return response.ok;
  };

  const flushQueue = async (useBeacon = false) => {
    if (isFlushing || queue.length === 0) return;
    isFlushing = true;
    try {
      while (queue.length > 0) {
        const batch = queue.splice(0, batchSize);
        try {
          const ok = await sendBatch(batch, useBeacon);
          if (!ok) {
            queue.unshift(...batch);
            break;
          }
        } catch {
          queue.unshift(...batch);
          break;
        }
      }
    } finally {
      isFlushing = false;
      if (queue.length > 0 && !useBeacon) {
        scheduleFlush();
      }
    }
  };

  const wrap =
    (level: ConsoleLevel, handler: (...args: unknown[]) => void) =>
    (...args: unknown[]) => {
      handler(...args);
      enqueue(buildPayload(level, args));
    };

  console.log = wrap("log", original.log);
  console.info = wrap("info", original.info);
  console.warn = wrap("warn", original.warn);
  console.error = wrap("error", original.error);
  console.debug = wrap("debug", original.debug);

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    if (url.includes("/logs/console")) {
      return originalFetch(input, init);
    }
    const method =
      (init?.method || (input instanceof Request ? input.method : undefined)) ??
      "GET";
    const start = Date.now();
    try {
      const response = await originalFetch(input, init);
      const durationMs = Date.now() - start;
      enqueue(
        buildPayload("debug", [
          "fetch",
          { url, method, status: response.status, ok: response.ok, durationMs },
        ])
      );
      return response;
    } catch (error) {
      const durationMs = Date.now() - start;
      enqueue(
        buildPayload("error", [
          "fetch_error",
          { url, method, durationMs },
          error,
        ])
      );
      throw error;
    }
  };

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target as HTMLElement | null;
      const resourceUrl =
        target && "src" in target ? (target as HTMLImageElement).src : null;
      const resourceHref =
        target && "href" in target ? (target as HTMLLinkElement).href : null;
      const detail = resourceUrl || resourceHref;
      if (detail) {
        enqueue(buildPayload("error", [`Resource load error: ${detail}`]));
        return;
      }
      if (event.error) {
        enqueue(buildPayload("error", [event.error]));
        return;
      }
      enqueue(buildPayload("error", [event.message || "Unknown window error"]));
    },
    true
  );

  window.addEventListener("unhandledrejection", (event) => {
    if (isAbortError(event.reason)) {
      console.debug("[DEBUG] Ignoring AbortError in unhandledrejection", {
        reason: event.reason,
      });
      event.preventDefault();
      return;
    }
    enqueue(
      buildPayload("error", ["Unhandled Promise Rejection", event.reason])
    );
  });

  window.addEventListener("pagehide", () => {
    void flushQueue(false);
  });
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushQueue(false);
    }
  });
};
